import React from 'react';
import {
  Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

import type { TrendDataPoint } from '../../api/dashboardApi';
import styles from './GrowthChart.module.css';

/**
 * How the corpus has grown, from `/dashboard/trends`.
 *
 * The monthly counts are real but spiky — one bulk ingest in January 2026 put
 * 625 instruments in at once, which flattens every other month to nothing and
 * reads as "the product stopped". The cumulative line is the same data asked a
 * fairer question: how much is in the corpus at each point in time. It is also
 * the claim the page is making, which the monthly bar is not.
 */

export interface GrowthPoint { period: string; total: number }

/** Monthly counts to a running total, trimmed to the last `months` points. */
export function cumulative(data: TrendDataPoint[], months = 24): GrowthPoint[] {
  let running = 0;
  const all = data.map((d) => {
    running += d.regulations_count || 0;
    return { period: d.period, total: running };
  });
  return all.slice(-months);
}

const short = (n: number) => (n >= 1000 ? `${Math.round(n / 100) / 10}k` : String(n));

interface Props {
  data: TrendDataPoint[];
  /**
   * recharts takes axis, grid and tooltip colours as props rather than CSS, so
   * this one component cannot inherit the ground the way everything else on
   * the page does. Left unset it follows the viewer's theme.
   */
  tone?: 'light' | 'dark';
}

/**
 * The viewer's theme, read from the document rather than from context.
 *
 * `useTheme` throws outside a ThemeProvider, and this component is rendered on
 * its own in tests and could be rendered anywhere else. The attribute is what
 * the provider sets, so watching it is both simpler and more robust — and the
 * observer means a theme switch repaints the chart rather than stranding it on
 * the colours of the ground it is no longer on.
 */
function useDocumentTheme(): 'light' | 'dark' {
  const read = () =>
    (typeof document !== 'undefined'
      && document.documentElement.getAttribute('data-theme') === 'dark')
      ? 'dark' as const
      : 'light' as const;
  const [theme, setTheme] = React.useState<'light' | 'dark'>(read);
  React.useEffect(() => {
    if (typeof MutationObserver === 'undefined') return;
    const mo = new MutationObserver(() => setTheme(read()));
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    setTheme(read());
    return () => mo.disconnect();
  }, []);
  return theme;
}

/** Axis, grid and label colours per ground. Fixed on dark for the same reason
 *  the band itself is fixed: it is dark in both themes on purpose. */
const TONES = {
  light: { axis: 'var(--ds-border)', label: 'var(--ds-text-muted)', panel: 'var(--ds-surface)' },
  dark: { axis: 'rgba(255,255,255,0.12)', label: 'rgba(255,255,255,0.48)', panel: 'var(--ds-dark-3)' },
} as const;

export const GrowthChart: React.FC<Props> = ({ data, tone }) => {
  const points = React.useMemo(() => cumulative(data), [data]);
  const resolved = useDocumentTheme();
  if (points.length < 2) return null;

  const c = TONES[tone ?? resolved];
  const first = points[0];
  const last = points[points.length - 1];
  const added = last.total - first.total;

  return (
    <figure className={`${styles.wrap} ${(tone ?? resolved) === 'dark' ? styles.dark : ''}`}>
      <figcaption className={styles.head}>
        <span className={styles.label}>Corpus over time</span>
        <span className={styles.delta}>
          +{added.toLocaleString()} since {first.period}
        </span>
      </figcaption>

      <div className={styles.chart}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id="growthFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--ds-link)" stopOpacity={0.35} />
                <stop offset="100%" stopColor="var(--ds-link)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke={c.axis} vertical={false} />
            <XAxis
              dataKey="period"
              tick={{ fontSize: 10, fill: c.label }}
              tickLine={false}
              axisLine={{ stroke: c.axis }}
              interval="preserveStartEnd"
              minTickGap={48}
            />
            <YAxis
              tick={{ fontSize: 10, fill: c.label }}
              tickLine={false}
              axisLine={false}
              width={38}
              tickFormatter={short}
            />
            <Tooltip
              cursor={{ stroke: c.axis }}
              contentStyle={{
                background: c.panel,
                border: `1px solid ${c.axis}`,
                borderRadius: 8,
                fontSize: 12,
              }}
              labelStyle={{ color: c.label }}
              formatter={(v) => [Number(v ?? 0).toLocaleString(), 'In the corpus']}
            />
            <Area
              type="monotone"
              dataKey="total"
              stroke="var(--ds-link)"
              strokeWidth={2}
              fill="url(#growthFill)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </figure>
  );
};

export default GrowthChart;
